import { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { PROFILE_AVATARS, PROFILE_COLORS, avatarFor, colorFor } from "@/lib/profileVisuals";
import { ft } from "@/lib/familyText";

export function ProfileAvatar({ profile, size = 96, selectable = false }) {
  const Icon = avatarFor(profile?.avatar);
  const c = colorFor(profile?.color);
  return (
    <div
      className={`relative rounded-2xl bg-gradient-to-br ${c.ring} flex items-center justify-center ${selectable ? "transition-transform group-hover:scale-105" : ""}`}
      style={{ width: size, height: size, boxShadow: `0 0 30px -6px ${c.glow}` }}
    >
      <div className="absolute inset-[3px] rounded-[0.85rem] bg-background/85 flex items-center justify-center">
        <Icon size={size * 0.42} className={c.text} strokeWidth={1.6} />
      </div>
    </div>
  );
}

export function ProfileEditor({ initial, onSave, onClose }) {
  const T = ft();
  const [name, setName] = useState(initial?.name || "");
  const [avatar, setAvatar] = useState(initial?.avatar || "spark");
  const [color, setColor] = useState(initial?.color || "amber");
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const submit = async () => {
    if (!name.trim()) { toast.error(T.nameRequired); return; }
    setBusy(true);
    try {
      await onSave({ name: name.trim(), avatar, color });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="profile-editor">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", damping: 24, stiffness: 260 }}
        className="relative w-full max-w-md glass border border-primary/25 rounded-[1.75rem] p-6 sm:p-8 frame-pulse"
      >
        <button
          data-testid="profile-editor-close"
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-foreground/50 hover:text-primary hover:bg-primary/10 transition-colors"
        >
          <X size={18} />
        </button>

        <div className="flex justify-center mb-5">
          <ProfileAvatar profile={{ avatar, color }} size={88} />
        </div>

        <label className="block text-xs uppercase tracking-wide text-foreground/50 mb-1.5">{T.name}</label>
        <input
          data-testid="profile-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={T.namePh}
          maxLength={24}
          className="w-full mb-5 rounded-xl bg-white/5 border border-white/12 px-4 py-3 text-foreground placeholder:text-foreground/30 focus:outline-none focus:border-primary/50"
        />

        <label className="block text-xs uppercase tracking-wide text-foreground/50 mb-2">{T.avatar}</label>
        <div className="grid grid-cols-6 gap-2 mb-5">
          {PROFILE_AVATARS.map((a) => {
            const Icon = a.Icon;
            const on = avatar === a.key;
            return (
              <button
                key={a.key}
                data-testid={`profile-avatar-${a.key}`}
                onClick={() => setAvatar(a.key)}
                className={`aspect-square rounded-xl flex items-center justify-center transition-colors ${on ? "bg-primary/20 border border-primary/60 text-primary" : "bg-white/5 border border-white/10 text-foreground/60 hover:text-primary"}`}
              >
                <Icon size={20} strokeWidth={1.7} />
              </button>
            );
          })}
        </div>

        <label className="block text-xs uppercase tracking-wide text-foreground/50 mb-2">{T.color}</label>
        <div className="flex flex-wrap gap-2.5 mb-7">
          {PROFILE_COLORS.map((c) => {
            const on = color === c.key;
            return (
              <button
                key={c.key}
                data-testid={`profile-color-${c.key}`}
                onClick={() => setColor(c.key)}
                className={`w-9 h-9 rounded-full bg-gradient-to-br ${c.ring} transition-transform ${on ? "ring-2 ring-white ring-offset-2 ring-offset-background scale-110" : "hover:scale-105"}`}
              />
            );
          })}
        </div>

        <button
          data-testid="profile-editor-save"
          onClick={submit}
          disabled={busy}
          className="w-full btn-frost-orange rounded-full py-3 font-semibold disabled:opacity-60"
        >
          {isEdit ? T.save : T.create}
        </button>
      </motion.div>
    </div>
  );
}
